import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isReady: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isReady: false,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const initializedRef = useRef(false);
  const isReadyRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Restore session first
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
      setIsReady(true);
      isReadyRef.current = true;
    });

    // Then listen for subsequent changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        if (!isReadyRef.current) {
          setLoading(false);
          setIsReady(true);
          isReadyRef.current = true;
        }

        // Sync locally stored push subscription on login
        if (_event === "SIGNED_IN" && currentSession?.user) {
          try {
            const stored = localStorage.getItem("push_subscription");
            if (stored) {
              const subJson = JSON.parse(stored);
              if (subJson.endpoint && subJson.keys?.p256dh && subJson.keys?.auth) {
                await supabase.from("push_subscriptions").upsert({
                  user_id: currentSession.user.id,
                  endpoint: subJson.endpoint,
                  p256dh: subJson.keys.p256dh,
                  auth: subJson.keys.auth,
                }, { onConflict: "user_id,endpoint" });
                localStorage.removeItem("push_subscription");
              }
            }
          } catch (e) {
            console.error("Failed to sync push subscription:", e);
          }
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      // Even if signOut fails, clear local state
      setSession(null);
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isReady, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
